import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/LangContext";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Buildings, ArrowLeft, Eye, EyeSlash, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";

export default function Login() {
  const { login, register, forgotPassword } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  // mode: signin | register | forgot | forgot-sent
  const [mode, setMode] = useState("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isRegister = mode === "register";
  const isForgot = mode === "forgot";
  const isSent = mode === "forgot-sent";
  const isSignin = mode === "signin";

  const switchMode = (m) => {
    setError("");
    setMode(m);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isRegister) {
        const u = await register(name.trim(), email.trim(), password);
        toast.success(t("login.register.welcome"));
        navigate(u.role === "admin" ? "/admin" : "/");
      } else if (isForgot) {
        await forgotPassword(email.trim());
        setMode("forgot-sent");
      } else {
        const u = await login(email.trim(), password);
        toast.success(t("login.welcome"));
        navigate(u.role === "admin" ? "/admin" : "/");
      }
    } catch (err) {
      const fallback = isRegister
        ? t("login.register.failed")
        : isForgot
        ? "Something went wrong. Please try again."
        : t("login.failed");
      const msg = formatApiErrorDetail(err.response?.data?.detail) || fallback;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const overline = isForgot || isSent
    ? t("login.forgot.overline")
    : isRegister
    ? t("login.register.overline")
    : t("login.overline");

  const title = isForgot
    ? t("login.forgot.title")
    : isSent
    ? t("login.forgot.sent.title")
    : isRegister
    ? t("login.register.title")
    : t("login.title");

  const subtitle = isForgot
    ? t("login.forgot.subtitle")
    : isSent
    ? t("login.forgot.sent.body")
    : isRegister
    ? t("login.register.subtitle")
    : t("login.subtitle");

  return (
    <div className="min-h-screen bg-[#f9f8f6] flex flex-col">
      <Navbar />
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-5">
        <aside className="hidden lg:block lg:col-span-3 relative border-r border-[#e5e3db] bg-[#1a1a1a]">
          <img
            src="https://images.unsplash.com/photo-1762774365027-12e4adc8350a?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80"
            alt="Kuching heritage"
            className="absolute inset-0 w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-[#1a1a1a]/85 via-[#1a1a1a]/40 to-transparent" />
          <div className="relative z-10 p-12 lg:p-16 h-full flex flex-col justify-between text-white">
            <div />
            <div className="max-w-xl">
              <div className="overline text-white/70 mb-4">Warisan Kuching</div>
              <h1 className="serif text-4xl lg:text-5xl xl:text-6xl font-black leading-[1.05] tracking-tight">
                {t("login.editorial.title.a")}<br />{t("login.editorial.title.b")}
              </h1>
            </div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-white/60">
              {t("login.editorial.footer")}
            </div>
          </div>
        </aside>

        <section className="lg:col-span-2 flex items-center justify-center px-6 py-14 lg:px-12">
          <div className="w-full max-w-md">
            <div className="flex items-center gap-3 mb-8">
              <span className="w-10 h-10 flex items-center justify-center bg-[#b25a38] text-white">
                <Buildings size={22} weight="duotone" />
              </span>
              <div className="leading-tight">
                <div className="serif text-lg font-bold text-[#1a1a1a]">Warisan Kuching</div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#5c5c5c]">
                  Heritage archive
                </div>
              </div>
            </div>

            {/* Mode tabs — hidden on forgot flow */}
            {(isSignin || isRegister) && (
              <div className="flex border border-[#e5e3db] bg-white mb-6" data-testid="login-mode-tabs">
                <button
                  type="button"
                  onClick={() => switchMode("signin")}
                  className={`flex-1 h-10 text-xs uppercase tracking-[0.18em] font-semibold border-r border-[#e5e3db] transition-colors duration-150 ${
                    mode === "signin" ? "bg-[#1a1a1a] text-white" : "text-[#5c5c5c] hover:text-[#1a1a1a]"
                  }`}
                  data-testid="login-tab-signin"
                  aria-pressed={mode === "signin"}
                >
                  {t("login.mode.signin")}
                </button>
                <button
                  type="button"
                  onClick={() => switchMode("register")}
                  className={`flex-1 h-10 text-xs uppercase tracking-[0.18em] font-semibold transition-colors duration-150 ${
                    mode === "register" ? "bg-[#1a1a1a] text-white" : "text-[#5c5c5c] hover:text-[#1a1a1a]"
                  }`}
                  data-testid="login-tab-register"
                  aria-pressed={mode === "register"}
                >
                  {t("login.mode.register")}
                </button>
              </div>
            )}

            <div className="overline mb-3">{overline}</div>
            <h2 className="serif text-3xl lg:text-4xl font-black text-[#1a1a1a] leading-tight">
              {title}
            </h2>
            <p className="mt-3 text-sm text-[#5c5c5c] leading-relaxed">{subtitle}</p>

            {isSent ? (
              <div className="mt-8 space-y-6" data-testid="forgot-sent">
                <div className="flex items-start gap-3 border border-[#c9d6a8] bg-[#eef4ea] p-4">
                  <CheckCircle size={22} weight="duotone" className="text-[#3f5119] flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-[#3f5119]">
                    <div className="font-semibold mb-1 flex items-center gap-1.5">
                      <EnvelopeSimple size={14} weight="duotone" /> Sent to {email}
                    </div>
                    Open the link in the email to choose a new password. Didn't get it?{" "}
                    <button
                      type="button"
                      onClick={() => switchMode("forgot")}
                      className="underline underline-offset-4 hover:text-[#1a1a1a]"
                      data-testid="forgot-resend-btn"
                    >
                      Try another email
                    </button>
                    .
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => switchMode("signin")}
                  className="w-full h-11 bg-[#b25a38] text-white text-sm uppercase tracking-[0.18em] font-semibold hover:bg-[#93492c] transition-colors duration-150"
                  data-testid="forgot-back-signin-btn"
                >
                  {t("login.forgot.back")}
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
                {isRegister && (
                  <div>
                    <label className="overline block mb-2">{t("login.name")}</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full h-11 px-3 text-sm bg-white border border-[#e5e3db] focus:border-[#b25a38] focus:outline-none focus:ring-1 focus:ring-[#b25a38] transition-colors"
                      placeholder="Nur Aisyah"
                      required
                      minLength={2}
                      data-testid="register-name-input"
                    />
                  </div>
                )}
                <div>
                  <label className="overline block mb-2">{t("login.email")}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-11 px-3 text-sm bg-white border border-[#e5e3db] focus:border-[#b25a38] focus:outline-none focus:ring-1 focus:ring-[#b25a38] transition-colors"
                    placeholder="you@heritage.local"
                    required
                    data-testid="login-email-input"
                  />
                </div>

                {!isForgot && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="overline">{t("login.password")}</label>
                      {isSignin && (
                        <button
                          type="button"
                          onClick={() => switchMode("forgot")}
                          className="text-xs text-[#b25a38] hover:text-[#93492c] hover:underline underline-offset-4 font-semibold"
                          data-testid="forgot-password-link"
                        >
                          {t("login.forgot.link")}
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <input
                        type={showPw ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full h-11 px-3 pr-11 text-sm bg-white border border-[#e5e3db] focus:border-[#b25a38] focus:outline-none focus:ring-1 focus:ring-[#b25a38] transition-colors"
                        required
                        minLength={isRegister ? 6 : undefined}
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPw((s) => !s)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5c5c5c] hover:text-[#1a1a1a]"
                        aria-label="Toggle password"
                        data-testid="login-toggle-password"
                      >
                        {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {isRegister && (
                      <p className="text-xs text-[#5c5c5c] mt-1.5">At least 6 characters.</p>
                    )}
                  </div>
                )}

                {error && (
                  <div
                    className="border border-[#f0c4be] bg-[#fbe4de] text-[#8a2b1e] text-sm px-3 py-2"
                    data-testid="login-error"
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 bg-[#b25a38] text-white text-sm uppercase tracking-[0.18em] font-semibold hover:bg-[#93492c] transition-colors duration-150 disabled:opacity-60"
                  data-testid={isForgot ? "forgot-submit-btn" : isRegister ? "register-submit-btn" : "login-submit-btn"}
                >
                  {loading
                    ? isForgot
                      ? t("login.forgot.submitting")
                      : isRegister
                      ? t("login.register.submitting")
                      : t("login.submitting")
                    : isForgot
                    ? t("login.forgot.submit")
                    : isRegister
                    ? t("login.register.submit")
                    : t("login.submit")}
                </button>

                {isForgot ? (
                  <div className="text-center text-sm text-[#5c5c5c] pt-2">
                    <button
                      type="button"
                      onClick={() => switchMode("signin")}
                      className="text-[#b25a38] font-semibold hover:text-[#93492c] hover:underline underline-offset-4"
                      data-testid="forgot-cancel-btn"
                    >
                      ← {t("login.forgot.back")}
                    </button>
                  </div>
                ) : (
                  <div className="text-center text-sm text-[#5c5c5c] pt-2">
                    {isRegister ? t("login.switch.toSignin") : t("login.switch.toRegister")}{" "}
                    <button
                      type="button"
                      onClick={() => switchMode(isRegister ? "signin" : "register")}
                      className="text-[#b25a38] font-semibold hover:text-[#93492c] hover:underline underline-offset-4"
                      data-testid="login-switch-mode-btn"
                    >
                      {isRegister ? t("login.switch.toSigninCta") : t("login.switch.toRegisterCta")}
                    </button>
                  </div>
                )}
              </form>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
